// Settings (docs/project.md §7; Settings board): profile, dictionaries, devices, sign out.
import { useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useFormatter, useLocale, useTranslations } from 'use-intl';
import { UI_LOCALES, type UiLocale } from '@solungo/core/schema';
import { languageTitle } from '@solungo/core/lang';
import { api } from '@/api/endpoints';
import { db, wipeDatabase } from '@/db/db';
import { queryClient, sessionsQuery } from '@/state/queries';
import { refreshDictionaries, setUser, useDictionaries, useUser } from '@/state/session';
import { useOnline } from '@/state/online';
import { storage } from '@/lib/storage';
import { describeUserAgent } from '@/lib/device';
import { dictionaryTitle, studentLabel } from '@/lib/dictionaryName';
import { LanguagePicker } from '@/components/LanguagePicker';
import { Book, ChevronRight, Device, Person } from '@/components/Icons';
import { Group, Section, Sheet, SheetBar, useCloseOverlay } from '@/components/ui';
import { reportError } from '@/lib/errors';
import { forgetUser, track } from '@/lib/analytics';

export const Route = createFileRoute('/_app/settings')({
  component: Settings,
});

function Settings() {
  const t = useTranslations('settings');
  const th = useTranslations('home');
  const tc = useTranslations('common');
  const locale = useLocale();
  const format = useFormatter();
  const navigate = useNavigate();
  const online = useOnline();
  const user = useUser();
  const dictionaries = useDictionaries();
  const sessions = useQuery({ ...sessionsQuery, enabled: online });
  const close = useCloseOverlay({ to: '/' });
  const [picker, setPicker] = useState<'locale' | 'native' | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(user?.name ?? '');

  const patch = useMutation({
    mutationFn: (body: Parameters<typeof api.me.patch>[0]) => api.me.patch(body),
    onSuccess: (res, body) => {
      setUser(res.user);
      const field = body.name !== undefined ? 'name' : body.locale !== undefined ? 'ui_locale' : 'native_lang';
      track('profile_updated', { field, value: body.name ?? body.locale ?? body.nativeLang ?? '' });
    },
  });
  const endSession = useMutation({
    mutationFn: (id: string) => api.me.deleteSession(id),
    onSuccess: () => {
      track('session_ended');
      void queryClient.invalidateQueries({ queryKey: sessionsQuery.queryKey });
    },
  });
  const leave = useMutation({
    mutationFn: (id: string) => api.dictionaries.leave(id),
    onSuccess: () => {
      track('dictionary_left');
      void refreshDictionaries();
    },
  });

  const signOut = async () => {
    track('signed_out');
    forgetUser();
    try {
      await api.auth.logout();
    } catch (e) {
      // the cookie may already be gone
      reportError(e);
    }
    queryClient.clear();
    await wipeDatabase(db);
    storage.clearAll();
    void navigate({ to: '/auth', replace: true });
  };

  if (!user) return null;
  const own = dictionaries.filter((d) => d.role === 'owner');
  const shared = dictionaries.filter((d) => d.role === 'teacher');

  return (
    <Sheet onClose={close} label={t('title')}>
      <SheetBar onClose={close} title={t('title')} />
      <div className="flex flex-col gap-[22px] overflow-y-auto px-4 pb-8 pt-3">
        <Section label={t('profile')} hint={t('profileHint')}>
          <Group>
            {editingName ? (
              <form
                className="flex min-h-[50px] items-center gap-3 px-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (name.trim() && name.trim() !== user.name) patch.mutate({ name: name.trim() });
                  setEditingName(false);
                }}
              >
                <span className="text-[17px]">{t('name')}</span>
                <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onBlur={(e) => e.currentTarget.form?.requestSubmit()} className="min-w-0 flex-grow bg-transparent text-right text-[17px] outline-none" data-testid="name-edit" />
              </form>
            ) : (
              <SettingRow label={t('name')} value={user.name ?? '—'} onClick={online ? () => setEditingName(true) : undefined} testId="name-row" />
            )}
            <SettingRow label={t('email')} value={user.email} />
            <SettingRow label={t('uiLocale')} value={languageTitle(user.locale, locale)} onClick={online ? () => setPicker('locale') : undefined} />
            <SettingRow label={t('nativeLang')} value={languageTitle(user.nativeLang, locale)} onClick={online ? () => setPicker('native') : undefined} />
          </Group>
        </Section>

        <Section label={t('dictionaries')} hint={shared.length ? t('dictionariesHint') : undefined}>
          <Group>
            {own.map((d) => (
              <Link key={d.id} to="/d/$dictionaryId/settings" params={{ dictionaryId: d.id }} className="flex min-h-[60px] items-center gap-3 py-2 pl-4 pr-3.5 text-ink">
                <Book className="flex-shrink-0 text-accent" />
                <span className="flex min-w-0 flex-grow flex-col gap-0.5">
                  <span className="text-[17px] leading-[22px]">{dictionaryTitle(d, locale)}</span>
                  <span className="text-[13px] leading-[17px] text-ink-secondary">{th('words', { count: d.wordCount })}</span>
                </span>
                <ChevronRight className="text-ink-quaternary" />
              </Link>
            ))}
            {shared.map((d) => (
              <div key={d.id} className="flex min-h-[60px] items-center gap-3 py-2 pl-4 pr-3">
                <Person className="flex-shrink-0 text-teacher" />
                <span className="flex min-w-0 flex-grow flex-col gap-0.5">
                  <span className="text-[17px] leading-[22px]">{dictionaryTitle(d, locale)}</span>
                  <span className="text-[13px] leading-[17px] text-ink-secondary">
                    {studentLabel(d, th as never)} · {th('words', { count: d.wordCount })}
                  </span>
                </span>
                <button
                  type="button"
                  disabled={!online || leave.isPending}
                  className="h-11 flex-shrink-0 whitespace-nowrap px-1 text-[15px] font-medium text-[#C8102E] disabled:opacity-40"
                  onClick={() => {
                    if (window.confirm(t('leaveConfirm', { name: dictionaryTitle(d, locale) }))) leave.mutate(d.id);
                  }}
                >
                  {t('leave')}
                </button>
              </div>
            ))}
          </Group>
        </Section>

        <Section label={t('devices')} hint={t('sessionsHint')}>
          <Group>
            {(sessions.data?.sessions ?? []).map((s) => (
              <div key={s.id} className="flex min-h-[60px] items-center gap-3 py-2 pl-4 pr-3" data-testid="session-row">
                <Device className="flex-shrink-0 text-ink-secondary" />
                <span className="flex min-w-0 flex-grow flex-col gap-0.5">
                  <span className="text-[17px] leading-[22px]">{describeUserAgent(s.userAgent)}</span>
                  <span className="text-[13px] leading-[17px] text-ink-secondary">
                    {s.current ? t('thisDevice') : format.dateTime(new Date(s.lastUsedAt), { day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit' })}
                  </span>
                </span>
                {!s.current && (
                  <button type="button" disabled={!online || endSession.isPending} className="h-11 flex-shrink-0 whitespace-nowrap px-1 text-[15px] font-medium text-[#C8102E] disabled:opacity-40" onClick={() => endSession.mutate(s.id)}>
                    {t('endSession')}
                  </button>
                )}
              </div>
            ))}
            {(sessions.data?.sessions.filter((s) => !s.current).length ?? 0) > 1 && (
              <button
                type="button"
                className="flex min-h-[50px] w-full items-center px-4 text-left text-[17px] text-accent"
                onClick={() => sessions.data?.sessions.filter((s) => !s.current).forEach((s) => endSession.mutate(s.id))}
              >
                {t('endOthers')}
              </button>
            )}
            {!online && <div className="px-4 py-3 text-[15px] text-ink-secondary">{tc('offline')}</div>}
          </Group>
        </Section>

        <Section label={t('account')}>
          <Group>
            <button type="button" className="flex min-h-[50px] w-full items-center px-4 text-left text-[17px] text-accent" data-testid="sign-out" onClick={() => void signOut()}>
              {t('signOut')}
            </button>
          </Group>
        </Section>
      </div>
      {picker && (
        <LanguagePicker
          title={picker === 'locale' ? t('uiLocale') : t('nativeLang')}
          value={picker === 'locale' ? user.locale : user.nativeLang}
          codes={picker === 'locale' ? UI_LOCALES : undefined}
          onSelect={(code) => {
            if (picker === 'locale') patch.mutate({ locale: code as UiLocale });
            else patch.mutate({ nativeLang: code });
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </Sheet>
  );
}

function SettingRow({ label, value, onClick, testId }: { label: string; value: string; onClick?: () => void; testId?: string }) {
  const inner = (
    <>
      <span className="flex-shrink-0 whitespace-nowrap text-[17px]">{label}</span>
      <span className="flex min-w-0 flex-grow items-center justify-end gap-2 text-[17px] text-ink-secondary">
        <span className="truncate">{value}</span>
        {onClick && <ChevronRight className="flex-shrink-0 text-ink-quaternary" />}
      </span>
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} data-testid={testId} className="flex min-h-[50px] w-full items-center gap-3 pl-4 pr-3.5 text-left">
      {inner}
    </button>
  ) : (
    <div className="flex min-h-[50px] items-center gap-3 pl-4 pr-3.5" data-testid={testId}>
      {inner}
    </div>
  );
}
